/**
 * FixThisModal - Bottom sheet showing quantified, actionable food suggestions
 * 
 * Tier-gated:
 * - Free: Generic suggestion + upsell
 * - Smart: 1-2 suggestions, no precise macros
 * - Genius/Trial: Full personalized, quantified, context-aware suggestions
 */

import React, { useState, useEffect } from 'react';
import { X, Zap, ChevronRight, Loader2, Plus, Search, Clock } from 'lucide-react';
import { brand } from '../../styles/brand';
import { getFixThisAccess, getLimitedSuggestion, INSIGHT_TYPES } from '../../utils/healthInsights';

const { colors, radius, spacing, typography } = brand;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FixThisModal = ({
  show,
  onClose,
  // Insight data
  insightType,
  headline,
  // User context
  userId,
  userTier = 'free',
  isTrial = false,
  // Macro context
  calories = 0,
  calorieGoal = 2000,
  protein = 0,
  proteinGoal = 150,
  fat = 0,
  fatGoal = 65,
  carbs = 0,
  carbsGoal = 250,
  // Smart context
  isFasting = false,
  fastingTimeRemaining = null,
  dietType = '',
  allergies = [],
  // Actions
  onAddMeal,
  onSmartMeals,
  onAskWhy,
  onUpgradeGenius
}) => {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fixAccess = getFixThisAccess(userTier, isTrial);
  const isOnTrack = insightType === INSIGHT_TYPES.ON_TRACK;

  // Fetch suggestions when modal opens
  useEffect(() => {
    if (!show || !insightType) return;
    if (fixAccess === 'upsell') return; // Free users get static content

    // Fasting users get static content
    if (isFasting) return;

    fetchSuggestions();
  }, [show, insightType]);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/health-intelligence/fix-this`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          insight_type: insightType,
          context: {
            calories, calorie_goal: calorieGoal,
            protein, protein_goal: proteinGoal,
            fat, fat_goal: fatGoal,
            carbs, carbs_goal: carbsGoal,
            is_fasting: isFasting,
            diet_type: dietType,
            allergies: allergies
          }
        })
      });

      if (!response.ok) throw new Error('Failed to load suggestions');
      const data = await response.json();
      setSuggestions(data);
    } catch (err) {
      console.error('[FixThis] Error:', err);
      setError('Could not load suggestions');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  // Compute quantified problem text
  const getQuantifiedProblem = () => {
    switch (insightType) {
      case INSIGHT_TYPES.PROTEIN_LOW: {
        const deficit = Math.round(proteinGoal - protein);
        return `You're ${deficit}g short on protein`;
      }
      case INSIGHT_TYPES.FAT_HIGH: {
        const over = Math.round(fat - fatGoal);
        return `Fats are ${over}g over your target`;
      }
      case INSIGHT_TYPES.CALORIES_HIGH: {
        const over = Math.round(calories - calorieGoal);
        return `You're ${over} kcal over`;
      }
      case INSIGHT_TYPES.CARBS_HIGH: {
        const over = Math.round(carbs - carbsGoal);
        return `Carbs are ${over}g over your target`;
      }
      case INSIGHT_TYPES.ON_TRACK:
        return "You're on track";
      default:
        return headline || '';
    }
  };

  // Format fasting time remaining
  const formatFastingTime = () => {
    if (!fastingTimeRemaining) return '';
    const hours = Math.floor(fastingTimeRemaining / 3600);
    const mins = Math.floor((fastingTimeRemaining % 3600) / 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div
      data-testid="fix-this-modal"
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 10001,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end'
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(4px)'
        }}
      />

      {/* Bottom Sheet */}
      <div style={{
        position: 'relative',
        background: '#1a1a2e',
        borderRadius: '20px 20px 0 0',
        maxHeight: '75vh',
        overflow: 'auto',
        animation: 'slideUp 0.25s ease-out'
      }}>
        {/* Handle */}
        <div style={{
          display: 'flex', justifyContent: 'center',
          padding: '10px 0 4px'
        }}>
          <div style={{
            width: '36px', height: '4px',
            borderRadius: '2px',
            background: 'rgba(255,255,255,0.15)'
          }} />
        </div>

        {/* Header */}
        <div style={{
          padding: '8px 20px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Zap size={18} style={{ color: isOnTrack ? '#38ef7d' : '#ffb74d' }} />
            <span style={{ fontSize: '17px', fontWeight: '700', color: 'white' }}>
              Fix This
            </span>
          </div>
          <button
            data-testid="fix-this-close"
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: 'none', borderRadius: '50%',
              width: '32px', height: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'rgba(255,255,255,0.6)'
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '0 20px 24px' }}>
          {/* === FASTING STATE === */}
          {isFasting ? (
            <FastingContent
              timeRemaining={formatFastingTime()}
              onViewLater={onClose}
              onAskWhy={() => {
                onClose();
                onAskWhy?.("I'm fasting right now. What should I eat when my eating window opens?", 'fasting');
              }}
            />
          ) : fixAccess === 'upsell' ? (
            /* === FREE TIER === */
            <FreeContent
              quantifiedProblem={getQuantifiedProblem()}
              insightType={insightType}
              onUpgrade={onUpgradeGenius}
            />
          ) : (
            /* === SMART / GENIUS / TRIAL === */
            <>
              {/* Quantified Problem */}
              <div style={{
                fontSize: '20px',
                fontWeight: '800',
                color: 'white',
                marginBottom: '4px',
                lineHeight: '1.3'
              }}>
                {getQuantifiedProblem()}
              </div>

              {/* Sub-label */}
              <div style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.45)',
                marginBottom: '20px'
              }}>
                {isOnTrack ? 'Keep going:' : 'Try one of these:'}
              </div>

              {/* Suggestions */}
              {loading ? (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '8px', padding: '32px 0', color: 'rgba(255,255,255,0.4)'
                }}>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: '13px' }}>Finding suggestions...</span>
                </div>
              ) : error ? (
                <div style={{
                  padding: '16px',
                  background: 'rgba(244, 67, 54, 0.1)',
                  borderRadius: radius.md,
                  fontSize: '13px',
                  color: '#f44336',
                  textAlign: 'center',
                  marginBottom: '16px'
                }}>
                  {error}
                </div>
              ) : suggestions?.items?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                  {suggestions.items.map((item, i) => (
                    <SuggestionRow key={i} item={item} isLimited={fixAccess === 'limited'} />
                  ))}
                </div>
              ) : (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: '13px',
                  marginBottom: '16px'
                }}>
                  Generating suggestions...
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  data-testid="fix-this-add-meal"
                  onClick={() => { onClose(); onAddMeal?.(); }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    borderRadius: radius.md,
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Plus size={16} />
                  Add Meal
                </button>
                <button
                  data-testid="fix-this-see-more"
                  onClick={() => { onClose(); onSmartMeals?.(); }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: radius.md,
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Search size={16} />
                  See More
                </button>
              </div>

              {/* Smart tier upsell hint */}
              {fixAccess === 'limited' && (
                <button
                  data-testid="fix-this-smart-upsell"
                  onClick={onUpgradeGenius}
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    padding: '10px',
                    background: 'rgba(102, 126, 234, 0.08)',
                    border: '1px solid rgba(102, 126, 234, 0.15)',
                    borderRadius: radius.md,
                    color: '#667eea',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  🧠 Get personalized suggestions with Genius Plaite
                  <ChevronRight size={14} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};


// === Sub-components ===

const SuggestionRow = ({ item, isLimited }) => (
  <div
    data-testid="fix-this-suggestion"
    style={{
      padding: '12px 14px',
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}
  >
    <span style={{ fontSize: '20px', flexShrink: 0 }}>{item.emoji || '🍽️'}</span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontSize: '14px',
        fontWeight: '600',
        color: 'white',
        lineHeight: '1.3',
        marginBottom: '2px'
      }}>
        {item.name}
      </div>
      {!isLimited && item.macro_hint && (
        <div style={{
          fontSize: '12px',
          color: 'rgba(255,255,255,0.45)',
          lineHeight: '1.3'
        }}>
          {item.macro_hint}
        </div>
      )}
    </div>
  </div>
);


const FastingContent = ({ timeRemaining, onViewLater, onAskWhy }) => (
  <div data-testid="fix-this-fasting">
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      marginBottom: '8px'
    }}>
      <Clock size={22} style={{ color: '#ff6b6b' }} />
      <span style={{ fontSize: '20px', fontWeight: '800', color: 'white' }}>
        You're currently fasting
      </span>
    </div>
    <p style={{
      margin: '0 0 20px',
      fontSize: '14px',
      color: 'rgba(255,255,255,0.55)',
      lineHeight: '1.5'
    }}>
      Save this for your eating window.
      {timeRemaining && ` Opens in ${timeRemaining}.`}
    </p>
    <div style={{ display: 'flex', gap: '10px' }}>
      <button
        data-testid="fix-this-view-later"
        onClick={onViewLater}
        style={{
          flex: 1, padding: '12px',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '12px',
          color: 'white', fontSize: '14px', fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        View Later
      </button>
      <button
        data-testid="fix-this-ask-why"
        onClick={onAskWhy}
        style={{
          flex: 1, padding: '12px',
          background: 'rgba(102, 126, 234, 0.12)',
          border: '1px solid rgba(102, 126, 234, 0.3)',
          borderRadius: '12px',
          color: '#667eea', fontSize: '14px', fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        Ask Why
      </button>
    </div>
  </div>
);


const FreeContent = ({ quantifiedProblem, insightType, onUpgrade }) => (
  <div data-testid="fix-this-free-tier">
    <div style={{
      fontSize: '20px', fontWeight: '800',
      color: 'white', marginBottom: '12px'
    }}>
      {quantifiedProblem}
    </div>
    <div style={{
      padding: '14px',
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '12px',
      marginBottom: '16px'
    }}>
      <p style={{
        margin: 0, fontSize: '14px',
        color: 'rgba(255,255,255,0.7)',
        lineHeight: '1.5'
      }}>
        {getLimitedSuggestion(insightType)}
      </p>
    </div>
    <button
      data-testid="fix-this-upgrade-btn"
      onClick={onUpgrade}
      style={{
        width: '100%',
        padding: '14px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: 'none',
        borderRadius: '12px',
        color: 'white', fontSize: '14px', fontWeight: '700',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
      }}
    >
      🧠 Get Genius Plaite
      <ChevronRight size={16} />
    </button>
  </div>
);


export default FixThisModal;
